import { z } from "zod";
import type { AppSettings, ChatMessage } from "../types";
import { DEFAULT_MODEL_ID, MODELS } from "../models";
import { defaultSettings } from "./prompt";

const settingsSchema = z.object({
  mode: z.enum(["fast", "medium", "thinking"]),
  contextTokens: z.union([z.literal(2048), z.literal(4096), z.literal(8192)]),
  historyLimit: z.union([z.literal(6), z.literal(12), z.literal(20)]),
  customContext: z.string().max(20_000),
  systemPrompt: z.string().max(20_000),
  ragEnabled: z.boolean().default(true),
  temperature: z.number().min(0).max(2),
  maxNewTokens: z.number().int().min(32).max(2048),
});

const attachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  mimeType: z.string(),
  size: z.number(),
  text: z.string(),
  truncated: z.boolean(),
});

const chatMessageSchema = z.object({
  id: z.string(),
  role: z.enum(["user", "assistant"]),
  content: z.string(),
  createdAt: z.number(),
  attachments: z.array(attachmentSchema).optional(),
  state: z.enum(["streaming", "complete", "error"]).optional(),
  metrics: z
    .object({
      tokens: z.number().optional(),
      tokensPerSecond: z.number().optional(),
      source: z.enum(["local", "swarm"]),
      nexusRoute: z.string().optional(),
      nexusTechId: z.string().optional(),
      ragSources: z.array(z.object({
        kind: z.string(),
        title: z.string(),
        url: z.string(),
      })).optional(),
    })
    .optional(),
});

const KEYS = {
  settings: "nexus-local.settings.v1",
  model: "nexus-local.model.v1",
  messages: "nexus-local.messages.v1",
  deviceName: "nexus-local.device-name.v1",
} as const;

function readJson(key: string): unknown {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

export function loadSettings(): AppSettings {
  const parsed = settingsSchema.safeParse(readJson(KEYS.settings));
  return parsed.success ? parsed.data : defaultSettings();
}

export function saveSettings(settings: AppSettings): void {
  localStorage.setItem(KEYS.settings, JSON.stringify(settings));
}

export function loadModelId(): string {
  const id = localStorage.getItem(KEYS.model);
  return MODELS.some((model) => model.id === id) ? (id as string) : DEFAULT_MODEL_ID;
}

export function saveModelId(id: string): void {
  localStorage.setItem(KEYS.model, id);
}

export function loadMessages(): ChatMessage[] {
  const parsed = z.array(chatMessageSchema).max(100).safeParse(readJson(KEYS.messages));
  return parsed.success ? parsed.data : [];
}

export function saveMessages(messages: ChatMessage[]): void {
  const completed = messages
    .filter((message) => message.state !== "streaming")
    .slice(-100)
    .map((message) => ({ ...message, state: message.state === "error" ? "error" : "complete" }));
  localStorage.setItem(KEYS.messages, JSON.stringify(completed));
}

export function loadDeviceName(): string {
  return localStorage.getItem(KEYS.deviceName)?.slice(0, 32) || "";
}

export function saveDeviceName(name: string): void {
  localStorage.setItem(KEYS.deviceName, name.slice(0, 32));
}

export function clearLocalData(): void {
  Object.values(KEYS).forEach((key) => localStorage.removeItem(key));
}
